import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr, fmtDate, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "@/components/Bits";
import { UploadButton } from "@/components/Upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Search, Download, Trash2, History, Upload as UploadIcon, PlusCircle } from "lucide-react";
import { toast } from "sonner";

const CATS = ["Employee Contracts", "Performance Reviews", "Payslips", "Medical Certificates", "Training Certificates", "HR Policies", "Company Documents", "Other"];

export default function Documents() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const [docs, setDocs] = useState([]);
  const [emps, setEmps] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [ver, setVer] = useState(null);
  const [form, setForm] = useState({ name: "", category: "Company Documents", description: "", tags: "", expiry_date: "", assign_type: "Company", assign_value: "", file_id: null, filename: "" });

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (cat !== "all") p.set("category", cat);
    api.get(`/documents?${p}`).then((r) => setDocs(r.data)).catch(() => {});
  }, [q, cat]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { if (canManage) api.get("/employees?status=Active").then((r) => setEmps(r.data)).catch(() => {}); }, [canManage]);

  const save = async () => {
    if (!form.file_id) { toast.error("Please upload a file first"); return; }
    try {
      await api.post("/documents", { ...form, tags: form.tags ? form.tags.split(",").map((t) => t.trim()) : [], assign_value: form.assign_value || null });
      toast.success("Document saved");
      setOpen(false); setForm({ name: "", category: "Company Documents", description: "", tags: "", expiry_date: "", assign_type: "Company", assign_value: "", file_id: null, filename: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (window.confirm("Delete this document?")) { await api.delete(`/documents/${id}`); load(); } };
  const addVersion = async (id, f) => { await api.post(`/documents/${id}/version?file_id=${f.id}&filename=${encodeURIComponent(f.filename)}`); toast.success("New version uploaded"); load(); };

  return (
    <div className="animate-fade-up">
      <PageTitle overline="Records" title="Documents">
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="upload-doc-btn"><PlusCircle className="h-4 w-4 mr-1.5" /> Upload Document</Button></DialogTrigger>
            <DialogContent className="bg-popover max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">Upload Document</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label>Document Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="doc-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="mt-1.5" data-testid="doc-category"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-popover">{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Expiry (optional)</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="mt-1.5" data-testid="doc-expiry" /></div>
                </div>
                <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" data-testid="doc-desc" /></div>
                <div><Label>Tags (comma separated)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-1.5" placeholder="onboarding, 2026" data-testid="doc-tags" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Assign To</Label>
                    <Select value={form.assign_type} onValueChange={(v) => setForm({ ...form, assign_type: v, assign_value: "" })}><SelectTrigger className="mt-1.5" data-testid="doc-assign-type"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-popover"><SelectItem value="Company">Company-wide</SelectItem><SelectItem value="Employee">Specific Employee</SelectItem><SelectItem value="Department">Department</SelectItem><SelectItem value="Division">Division</SelectItem></SelectContent></Select></div>
                  {form.assign_type === "Employee" && (
                    <div><Label>Employee</Label>
                      <Select value={form.assign_value} onValueChange={(v) => setForm({ ...form, assign_value: v })}><SelectTrigger className="mt-1.5" data-testid="doc-assign-emp"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent className="bg-popover max-h-64">{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent></Select></div>
                  )}
                  {(form.assign_type === "Department" || form.assign_type === "Division") && (
                    <div><Label>{form.assign_type}</Label><Input value={form.assign_value} onChange={(e) => setForm({ ...form, assign_value: e.target.value })} className="mt-1.5" placeholder={form.assign_type} data-testid="doc-assign-val" /></div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <UploadButton accept="*/*" label={form.file_id ? `Uploaded: ${form.filename}` : "Upload File"} done={!!form.file_id} onUploaded={(f) => setForm({ ...form, file_id: f.id, filename: f.filename, name: form.name || f.filename })} />
                </div>
              </div>
              <DialogFooter><Button onClick={save} disabled={!form.name || !form.file_id} data-testid="doc-save">Save Document</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageTitle>

      <div className="flex gap-3 mb-6">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or tag…" className="pl-9" data-testid="doc-search" /></div>
        <Select value={cat} onValueChange={setCat}><SelectTrigger className="w-52" data-testid="doc-filter-cat"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover"><SelectItem value="all">All Categories</SelectItem>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {docs.length === 0 && <p className="col-span-full text-muted-foreground text-center py-12">No documents found.</p>}
        {docs.map((d) => (
          <Card key={d.id} className="p-5 rounded-md border-border hover:-translate-y-1 hover:shadow-md transition-transform duration-200" data-testid={`doc-${d.id}`}>
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-md bg-primary/10 flex items-center justify-center shrink-0"><FileText className="h-5 w-5 text-primary" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold truncate">{d.name}</p>
                <p className="overline mt-0.5">{d.category}</p>
                {d.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{d.description}</p>}
                <div className="flex flex-wrap gap-1 mt-2">{(d.tags || []).map((t) => <span key={t} className="px-1.5 py-0.5 rounded bg-muted text-[10px] text-muted-foreground">{t}</span>)}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border">
              <span className="text-xs text-muted-foreground flex-1">{d.expiry_date ? `Expires ${fmtDate(d.expiry_date)}` : `Added ${fmtDate(d.created_at)}`} · v{(d.versions || []).length}</span>
              <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer"><Button size="icon" variant="outline" className="h-8 w-8" data-testid={`doc-dl-${d.id}`}><Download className="h-4 w-4" /></Button></a>
              {canManage && <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setVer(d)} data-testid={`doc-ver-${d.id}`}><History className="h-4 w-4" /></Button>}
              {user.role === "admin" && <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" onClick={() => del(d.id)} data-testid={`doc-del-${d.id}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!ver} onOpenChange={(o) => !o && setVer(null)}>
        <DialogContent className="bg-popover max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Version History — {ver?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(ver?.versions || []).slice().reverse().map((v, i) => (
              <div key={i} className="flex items-center justify-between p-2 rounded-md border border-border text-sm">
                <div><p className="font-medium">v{(ver.versions.length - i)} · {v.filename}</p><p className="overline">{fmtDate(v.uploaded_at)} · {v.uploaded_by}</p></div>
                <a href={fileUrl(v.file_id)} target="_blank" rel="noreferrer"><Button size="icon" variant="ghost" className="h-8 w-8"><Download className="h-4 w-4" /></Button></a>
              </div>
            ))}
          </div>
          {canManage && ver && <div className="mt-3"><UploadButton accept="*/*" label="Upload New Version" onUploaded={(f) => { addVersion(ver.id, f); setVer(null); }} /></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
